import fetch from 'node-fetch'
import router from '@/router'
import { clearStorage } from '@/utils/storageManager';

const httpHeaders = {
    'Content-Type': 'application/json; charset=UTF-8',
}

/**
 * 非 2xx 的處理。
 *
 * ⚠ **預設會 `router.push` 換頁**（400／403／500 一律去 `/error`），所以呼叫端的 catch
 * 幾乎做不了事——它跑的時候畫面已經換掉了。這是全站既有行為，**不在這一票改**：
 * 既有畫面多半沒接 catch，改成不換頁會讓它們從「跳錯誤頁」變成「靜默無反應」，那更糟。
 *
 * 要在原地顯示錯誤（例如素材庫的上傳對話框要說「配額滿了」）的呼叫端傳
 * `{ handleError: true }` opt out。
 *
 * @param {Response} res
 * @param {{handleError?: boolean}} [options] `handleError` = 呼叫端自己處理，不換頁。
 *   ⚠ **401 仍然換頁**——session 過期不是對話框能解決的事，一定要送去重新登入。
 */
const checkStatus = (res, options) => {
    if (res.status >= 200 && res.status < 300) {
        return res
    } else {
        let error = new Error(res.statusText)
        /// 呼叫端要分辨 403（配額滿）／409（下架未確認）／400（參數錯）才能給出對的訊息，
        /// 而 `Error` 只帶 statusText——後端多半沒設，拿到的是空字串。所以把碼掛上去。
        error.status = res.status
        if (options && options.handleError && res.status !== 401) {
            throw error
        }
        let path = '/error';
        switch (res.status) {
            case 404: path = '/404'; break;
            case 401:
                if (res.url.indexOf('book') != -1) {
                    path = '/ebook/login';
                } else {
                    path = '/login/check';
                    clearStorage();
                }
                sessionStorage.setItem('redirect_path', window.location.pathname);
                break;
        }
        router.push(path);
        throw error;
    }
}

export default {
    /**
     * @param {string} url
     * @param {{handleError?: boolean}} [options] 見 checkStatus。不給 = 既有行為。
     */
    async get(url, options) {
        try {
            const res = await fetch(url, {
                method: 'get',
                headers: httpHeaders,
                credentials: 'include'
            })
            const data = checkStatus(res, options)
            return data.json()
        } catch (err) {
            console.log(`client failed to get ${url}, err: ${err}`)
            throw err
        }
    },
    async getBinary(url) {
        try {
            const res = await fetch(url, {
                method: 'get',
                headers: httpHeaders,
                credentials: 'include',
                dataType: 'binary',
                xhrFields: {
                    responseType: 'blob'
                },
            })
            //const data = checkStatus(res)
            return res.blob()
        } catch (err) {
            console.log(`client failed to get ${url}, err: ${err}`)
            throw err
        }
    },
    /**
     * @param {string} url
     * @param {Object} d
     * @param {{handleError?: boolean}} [options] 見 checkStatus。不給 = 既有行為。
     */
    async post(url, d, options) {
        try {
            return fetch(url, {
                method: 'post',
                keepalive: true,
                body: JSON.stringify(d),
                headers: httpHeaders,
                credentials: 'include'
            }).then(res => {
                const data = checkStatus(res, options)
                return data.json()
            })

        } catch (err) {
            console.log(`client failed to post ${url}, err: ${err}`)
            throw err
        }
    },

    /**
     * @param {string} url
     * @param {Object} d
     * @param {{handleError?: boolean}} [options] 見 checkStatus。不給 = 既有行為。
     */
    async postFormData(url, d, options) {
        try {
            const headers = Object.assign({}, httpHeaders)
            delete headers['Content-Type'];

            const formData = new FormData();
            for (let key in d) {
                formData.append(key, d[key]);
            }
            return fetch(url, {
                method: 'post',
                body: formData,
                headers: headers,
                credentials: 'include'
            }).then(res => {
                const data = checkStatus(res, options)
                return data.json()
            })

        } catch (err) {
            console.log(`client failed to postFormData ${url}, err: ${err}`)
            throw err
        }
    },
    /**
     * @param {string} url
     * @param {Object} [d] 送 body 的 DELETE。⚠ 只有給值時才會帶 body——
     *   既有呼叫端全部只傳 url，行為不變。素材下架要帶 `{ confirmed: true }`（規格 §5.2）。
     * @param {{handleError?: boolean}} [options] 見 checkStatus。不給 = 既有行為。
     */
    async delete(url, d, options) {
        try {
            const init = {
                method: 'delete',
                headers: httpHeaders,
                credentials: 'include'
            }
            if (d !== undefined) {
                init.body = JSON.stringify(d)
            }
            const res = await fetch(url, init)
            const data = checkStatus(res, options)
            return data.json()
        } catch (err) {
            console.log(`client failed to delete ${url}, err: ${err}`)
            throw err
        }
    },
    /**
     * 回一份丟掉 `null` / `undefined` 鍵的複本。
     *
     * ⚠ **`queryParams` 與 `postFormData` 都是無條件走過每一個鍵**，所以 nullish 會變成
     * 字面字串：查詢字串出現 `lang=undefined`，FormData 出現 `description="null"`
     * （存進 DB 就是四個字母）。**送出前先過這一支。**
     *
     * ⚠ 刻意**不**改 `queryParams` / `postFormData` 自己去跳過 nullish——那會動到全站
     * 每一個呼叫端；有些搞不好正靠著現在的行為。要收斂就另開一票。
     */
    omitNullish(obj) {
        const out = {}
        Object.keys(obj).forEach(k => {
            if (obj[k] !== null && obj[k] !== undefined) {
                out[k] = obj[k]
            }
        })
        return out
    },

    queryParams(params) {
        return Object.keys(params)
            .map(k => encodeURIComponent(k) + '=' + encodeURIComponent(params[k]))
            .join('&');
    },

    resourceUrl(r) {
        const resource = {
            // consoleLogin: '/console/signIn',
            // consoleLogout: '/console/logout',
            // consoleAdminName: '/console/my',
            // consoleChangePwd: '/console/changePwd',
            // consoleSysFunction: '/console/sysFunction',
            // consoleAdminUser: '/console/adminUser',
            // consoleAdminUserRoleList: '/console/adminUser/roles',
            // consoleContestStat: '/console/contest/stat',
            // consoleUserList: '/console/users',
            // consoleUserCheckSchool: '/console/users/:id',
            // consoleContestRoundPostList: '/console/contest/:round/post',
            // consoleContesPostScoring: '/console/contest/post/:id/score',
            // consoleContesPost: '/console/contest/post/:id',

            loginLink: '/login',
            logoutLink: '/logout',
            login: '/console/signIn',
            dcLogin: '/console/dcSignIn',
            myInfo: '/console/my',
            userInfo: '/user/info',
            userMenu: '/console/system/menu',
            userLogout: '/user/logout',
            editAccountLink: '/account',
            refreshUserInfo: '/refresh',
            changeMode: '/changeMode',
            accountLogin: '/console/accountLogin',
            userBasicInfoSetting: '/user/setting/basic',
            emailValidate: '/user/email/validate',
            emailCodeValidate: '/user/email/validate/code',
            userSchoolSetting: '/user/setting/school',
            changeLanguage: '/user/changeLang/:lang',
            getKey: '/user/key',
            changePwd: '/user/pwd',
            bindingCheck: '/user/bindingCheck',
            forgetPWD: '/user/forget/pwd/:validateWay',
            resetPwd: '/user/pwd/reset',
            checkResetPwd: '/user/pwd/reset/:code',
            updateAvatar: '/user/avatar',
            addNotifyToken: '/user/notifyToken',
            removeNotifyToken: '/user/notifyToken/remove',

            checkAccount: '/register/check',
            register: '/register/',
            get3rdOAuthLink: '/oAuth/:platform',
            get3rdOAuthBindLink: '/oAuth/:platform/bind',
            oAuthLogin3rd: '/oAuth/login/:platform',
            bind3rdAccount: '/register/bind',

            getTeam: '/team/:id',
            cancelTeamPlan: '/team/plan/:teamHistoryId/cancel',
            checkTeamMember: '/team/:teamId/teamMemver/check',
            addTeamMember: '/team/:teamId/teamMember',
            changeMemberRole: '/team/:teamId/teamMember/:userId/role',
            removeTeamMember: '/team/:teamId/teamMember/:userId/remove',

            getMyVouchers: '/voucher/my',
            redeemVoucher: '/voucher/redeem',
            useVoucher: '/voucher/use/:id',

            getMyInviteCode: '/my/inviteCode',
            getMyInvitedFriends: '/my/invited',

            getDiaryFunctionPrivilege: '/console/diary/funcPrivilege',
            getTrendData: '/console/diary/homework/:id/statistic/trend',
            getTrendDataPost: '/console/diary/homework/:id/statistic/trend/post',
            getEmotion4Data: '/console/diary/homework/:id/statistic/emotion4',
            getEmotion4DataPost: '/console/diary/homework/:id/statistic/emotion4/post',
            getEmotionsData: '/console/diary/homework/:id/statistic/emotions',
            getEmotionsDataPost: '/console/diary/homework/:id/statistic/emotions/post',
            getBehaviorsData: '/console/diary/homework/:id/statistic/behaviors',
            getBehaviorsDataPost: '/console/diary/homework/:id/statistic/behaviors/post',

            getHomeworkPost: '/console/diary/post/:id',

            createHomework: '/console/diary/homework/:id',
            getHomeworkDetail: '/console/diary/homework/:id/detail',
            getHomeworkName: '/console/diary/homework/:id',
            getHomeworkStudentName: '/console/diary/homework/:id/users',
            exportStudentEmails: '/console/diary/homework/:id/students/emails',
            getHomeworkPostPagingData: '/console/diary/homework/:id/posts',
            getHomeworkRecentPostUser: '/console/diary/homework/:id/recent',
            getHomewrokInviteCode: '/homework/:diaryHomeworkId/inviteCode',
            checkHomewrokInviteCode: '/homework/:diaryHomeworkId/check/inviteCode',
            checkHomeworkTeacherInviteCode: '/homework/:diaryHomeworkId/check/teacherInviteCode',
            getHomeworkTeacherInvitation: '/homework/:diaryHomeworkId/invitationLetter',
            getHomeworkTeachers: '/homework/:diaryHomeworkId/privilege',
            getBadMoodStudents: '/homework/:diaryHomeworkId/badMood/member',
            acceptHomeworkPrivilege: '/homework/:diaryHomeworkId/privilege/accept',
            rejectHomeworkPrivilege: '/homework/:diaryHomeworkId/privilege/reject',
            deleteHomeworkPrivilege: '/homework/:diaryHomeworkId/privilege/delete',
            checkJoinedHomework: '/console/diary/homework/:diaryHomeworkId/isJoined',
            getDiaryHomeworkUserWithUsage: '/homework/student/active/usage',
            applyInvitationCode: '/drpb/:code',

            getDiaryPostComment: '/console/diary/post/:id/comment',
            createDiaryPostComment: '/console/diary/post/:id/comment',
            updateDiaryPostComment: '/console/diary/post/comment/:id',
            deleteDiaryPostComment: '/console/diary/post/comment/:id',
            // testDiary: '/ajax/diary',

            exportRaw: '/export/raw',
            exportBCard: '/ai/multipleResult',
            exportFormResponses: '/export/forms/responses',
            exportFormList: '/export/hasForm',

            allStudentList: '/student/',
            getBasicInfo: '/student/basicInfo',
            uploadStudents: '/student/upload',
            uploadStudentsCheck: '/student/upload/check',
            uploadStudentsSubmit: '/student/upload/submit',
            deleteStudent: '/student/delete',
            students: '/student/list/:id',
            studentCase: '/student/:homeworkId/case/:studentId',
            studentDetail: '/student/info/:id',
            studentPosts: '/student/diaryPostHistory',
            studentCaseList: '/student/:homeworkId/case',
            archiveStudentFromHomework: '/student/:homeworkId/archiveStudent',

            sendBackPosts: '/student/:homeworkId/sendback',

            postCollectionUpdate: '/post/:diaryPostId/collect',
            postCollectionList: '/student/:diaryHomeworkId/collection',
            sendBackDiaryPost: '/post/:diaryPostId/sendback',
            getDiaryPostImage: '/post/:diaryPostId/composeAll',

            getAIStrategy: '/ai/simpleReply/:id',

            getDiaryHomework: '/diary/homework',
            getMyHomeworkList: '/diary/homework/my',
            copyDiaryHomework: '/homework/:diaryHomeworkId/copy',
            archiveDiaryHomework: '/homework/:diaryHomeworkId/archive',
            getJoinedDiaryHomework: '/diary/homework/others',
            joinDiaryHomework: '/diary/homework/join',
            getArchivedDiaryHomework: '/diary/homework/archive',
            getDiaryHomeworkCase: '/diary/homework/case',

            getPublishSampleHomework: '/diary/homework/publishSample',
            getPublishSampleHomeworkWithSample: '/diary/homework/publishSample/resourcesBook',
            getPublishSampleHomeworkResources: '/diary/homework/publishSample/:diaryHomeworkId/resources',
            takePublishSampleHomeworkPlan: '/diary/homework/publishSample/:id/takePlan',
            getHomeworkResource: '/homework/:diaryHomeworkId/resources',

            getSampleQuest: '/diary/:diaryHomeworkId/sampleQuest',
            getSampleQuestDetail: '/diary/:diaryHomeworkId/sampleQuest/:questId',
            getQusetDiary: '/diary/questSample',

            // 關卡式範本編輯器（見 docs/plans/UnitTemplateEditor.web.spec.md）
            getTemplateUnitList: '/diary/:diaryHomeworkId/units',
            addTemplateUnit: '/diary/:diaryHomeworkId/units',
            reorderTemplateUnits: '/diary/:diaryHomeworkId/units/order',
            /// ⚠ 2026-08-11 由 :questId 改為 :unit——刪的是整個關卡
            deleteTemplateUnit: '/diary/:diaryHomeworkId/units/:unit',
            saveTemplateUnit: '/diary/:diaryHomeworkId/units/:unit',
            addTemplateQuest: '/diary/:diaryHomeworkId/units/:unit/quests',
            reorderTemplateQuests: '/diary/:diaryHomeworkId/units/:unit/quests/order',
            deleteTemplateQuest: '/diary/:diaryHomeworkId/quests/:questId',
            questThumbnail: '/diary/:diaryHomeworkId/quests/:questId/thumbnail',
            questAudioStatus: '/diary/:diaryHomeworkId/quests/:questId/audio',
            /// 範本分享代碼（studio-api docs/plans/TemplateShare.spec.md）
            templateShareCode: '/diary/:diaryHomeworkId/shareCode',
            claimTemplateShareCode: '/diary/template/claim',
            getTemplateMapPreview: '/diary/:diaryHomeworkId/map',
            getAvailableMaps: '/diary/maps',

            // 素材庫（見 mangachat-studio-api docs/plans/MediaLibrary.spec.md §5.2）
            /// GET = 瀏覽清單；POST = 上傳。同一個路徑兩個動作，比照 questThumbnail 的命名
            media: '/diary/media',
            /// 「我的素材」每個分類各有幾筆（F10 改版）。⚠ 固定 scope=mine，
            /// 不收 scope——挑選器不需要筆數，放開會變成能窺探別人素材分布的端點。
            mediaCounts: '/diary/media/counts',
            mediaQuota: '/diary/media/quota',
            mediaYoutube: '/diary/media/youtube',
            /// 字表**沒有檔案**，所以是「建立」而不是上傳（JSON，不是 multipart）。
            /// `POST /diary/media` 收到 wordlist 會明確回 400。
            mediaWordlist: '/diary/media/wordlist',
            /// 改字表內容。⚠ 與 `mediaItem` 是**兩支**：那一支只改中介資料、一定成功；
            /// 這一支會改變已經引用這份字表的題目行為，所以可能被後端拒絕。
            mediaItemWordlist: '/diary/media/:mediaId/wordlist',
            /// 改找不同圖的熱區。⚠ 與 `mediaItemWordlist` 同一類（改素材內容），
            /// 但**不會**因為引用它的題目而失敗——移動框不會讓任何題目變成不合法。
            mediaItemHotspot: '/diary/media/:mediaId/hotspot',
            /// POST = 改標題／說明／可見範圍；DELETE = 下架 + 連鎖停用（後端 B11 才有）
            mediaItem: '/diary/media/:mediaId',
            mediaImpact: '/diary/media/:mediaId/impact',
            mediaRelist: '/diary/media/:mediaId/relist',

            /// 被素材下架卡住的題目（F12）。⚠ `summary` 掛在版面層的橫幅上、
            /// **每一頁都會打**，所以它只回兩個數字；清單才 join 素材。
            blockedQuestSummary: '/diary/quests/blocked/summary',
            blockedQuestList: '/diary/quests/blocked',

            takeCourseBook: '/homework/takeCourseBook',

            getPostRevise: '/post/:diaryPostId/revise',
            editPostRevise: '/post/:diaryPostId/revise/:commentId',
            getPostReviseLibrary: '/post/revise/library',
            editReviseLibraryComment: '/post/revise/library/:id',

            // getDiaryQuestList: '/homeworkQuest/:diaryHomeworkId/questList',
            getHomeworkQuestDetail: '/homework/:id/quest/:qid/',
            getQuestPostPaging: '/homeworkQuest/:diaryHomeworkId/quest/:questId/posts',
            getQusetSelection: '/homeworkQuest/:diaryHomeworkId/questSelection',
            getQuestInfo: '/homework/:id/quest/info',

            getAssessmentResultList: '/homework/:diaryHomeworkId/quiz/results',
            getAssessmentResult: '/homework/:diaryHomeworkId/quiz/result/:uuid',
            getAssessmentResultDownload: '/homework/:diaryHomeworkId/quiz/result/download',
            
            takeContestBook: '/contest/takeHomework',
            getContestHomeworkInfo: '/contest/:id/info',
            contestInviteCoderVerify: '/contest/inviteCodeVerify',
            checkHomeworkContest: '/diary/:diaryHomeworkId/contest',
            getTeamRanking: '/contest/:id/ranking',
            getStudentRanking: '/contest/:id/studentRanking',
            getTeamInfo: '/contest/team/:id/info',

            getSafetyAlerts: '/console/safety/alerts',
            getSafetySummary: '/console/safety/summary',
            getSafetyAlertsByPosts: '/console/safety/posts',
            updateSafetyAlertsByPost: '/console/safety/post/:postId',

            getNotificationIndex: '/wn/',
            notificationRead: '/wn/notification/:id',
            getUnreadNotification: '/wn/unnotify',
            clearNotification: '/wn/clear',

            getMyOwnChargePlanList: '/ucp',
            soldUserChargePlan: '/ucp/:id/sold',
            releaseUserChargePlan: '/ucp/:id/release',
            getUnsoldUserChargePlanCount: '/ucp/unsold',
            userChargePlanSetNewCode: '/ucp/:id/code',
            userChargePlanAddComment: '/ucp/:id/comment',
            renewUserChargePlan: '/ucp/:id/renew',
            getHomeworkNonUseUserChargePlanCount: '/ucp/unuse/homeworkPlan',

            getEBook: '/book/homework/:diaryHomeworkId/user/:userId',
            getStudentEBook: '/book/diary/:diaryId',
            getEBookPageList: '/book/:diaryPrintId/pages',
            getEBookPageTemplate: '/book/template/:diaryPrintTemplateId',
            saveEBookPageTemplate: '/book/template/:diaryPrintTemplateId',
            saveEBookPageTemplateImage: '/book/template/:diaryPrintId/image',
            getEBookTemplateList: '/book/template',
            getEBookPDF: '/book/:diaryPrintId/ebook',
            generateEBookPDF: '/book/:diaryPrintId/generate',
            loginLinkEBook: '/loginLink/ebook',
            eBookUserInfo: '/ebookUser/info',
            getBookList: '/book/',
            getEBookDiaryPosts: '/book/:diaryPrintId/posts',
            getEBookSelectedPosts: '/book/:diaryPrintId/pages',
            getEBookPostDetail: '/book/post/:id',
            updateEBookPosts: '/book/:diaryPrintId',
            addEBookPage: '/book/:diaryPrintId/page',
            getEBookBasicInfo: '/book/:diaryPrintId/basicInfo',
            saveEBookBasicInfo: '/book/:diaryPrintId/basicInfo',
            getEBookLink: '/book/:diaryPrintId/link',

        }
        
        let domainName = process.env.VUE_APP_API_URL;
        if (location.origin.indexOf('ebook.mangachat') != -1) {
            domainName = process.env.VUE_APP_EBOOK_API_URL
        }
        
        let url = domainName ? `${domainName}${resource[r]}` : resource[r]
        return url
    }
}